import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { InputField } from '../form-controls';
import { OptionNumberField } from './commitment-fields';
import { PriceRangesList } from './price-ranges-list';
import { TemplateSection, TemplateStack } from './template-shell';
import {
  readProductConfigOptions,
  toServiceConfigOption,
} from './product-config-option';
import type { ContractFormValues } from '../contract-form';

/**
 * Mlytics / generic-config template — the fallback for VPS, Email, Hosting,
 * Colocation and Mlytics products. Seeds one `service_configoptions[]` entry per
 * `product_config_options` item from `/products/:id`, then renders each as a
 * config card (value, price-per-unit, optional statement pricing). Also exposes
 * order-level prepaid + register price.
 */
export function MlyticsConfigTemplate({ index, detail }: { index: number; detail: unknown }) {
  const { control, setValue, getValues } = useFormContext<ContractFormValues>();
  const optsPath = `products.${index}.service_configoptions` as const;
  const options = useWatch({ control, name: optsPath }) ?? [];
  const prepaid = useWatch({ control, name: `products.${index}.prepaid` }) ?? true;

  const configOptions = readProductConfigOptions(detail);
  const seededFor = useRef<string>('');

  // Prepaid is a boolean field (backend `prepaid bool`). Persist the choice AND
  // mirror it onto every option's `commitment_prepaid` (legacy prepaid select).
  const onPrepaidChange = (value: string) => {
    const next = value === 'true';
    setValue(`products.${index}.prepaid`, next, { shouldDirty: true });
    const current = getValues(optsPath);
    if (Array.isArray(current) && current.length) {
      setValue(
        optsPath,
        current.map((o) => ({ ...o, commitment_prepaid: next })) as never,
        { shouldDirty: true },
      );
    }
  };

  // Seed the config options from the product's catalog options (once per product).
  useEffect(() => {
    const key = `${index}:${configOptions.map((o) => o.name).join(',')}`;
    if (!configOptions.length || seededFor.current === key) return;
    const current = getValues(optsPath);
    if (Array.isArray(current) && current.length > 0 && seededFor.current !== '') return;
    seededFor.current = key;
    const prepaid = getValues(`products.${index}.prepaid`) ?? true;
    setValue(
      optsPath,
      configOptions.map((o) => toServiceConfigOption(o, prepaid)) as never,
      { shouldDirty: true },
    );
  }, [configOptions, index, optsPath, getValues, setValue]);

  return (
    <TemplateStack>
      {(options as unknown[]).map((_, i) => (
        <ConfigOptionCard key={i} index={index} optionIndex={i} />
      ))}

      <TemplateSection title="Registration">
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <Field label="Prepaid">
            {(p) => (
              <Select value={prepaid ? 'true' : 'false'} onValueChange={onPrepaidChange}>
                <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Prepay</SelectItem>
                  <SelectItem value="false">Pay later</SelectItem>
                </SelectContent>
              </Select>
            )}
          </Field>
          <InputField name={`products.${index}.service_price_register`} label="Register price" type="number" />
        </div>
      </TemplateSection>
    </TemplateStack>
  );
}

function ConfigOptionCard({ index, optionIndex }: { index: number; optionIndex: number }) {
  const { control } = useFormContext<ContractFormValues>();
  const optPath = `products.${index}.service_configoptions.${optionIndex}` as const;
  const name = useWatch({ control, name: `${optPath}.name` as never }) as string | undefined;
  const statementable = (useWatch({ control, name: `${optPath}.statementable` as never }) as unknown) === true;
  const commitmentValue = Number(useWatch({ control, name: `${optPath}.commitment_value` as never }) ?? 0);

  return (
    <TemplateSection title={(name ?? 'Option').toUpperCase()}>
      <div style={{ display: 'grid', gap: 'var(--vn-space-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
        <OptionNumberField path={`${optPath}.commitment_value`} label="Value" />
        <OptionNumberField path={`${optPath}.commitment_price_per_unit`} label="Price per unit" />
      </div>
      {statementable && <PriceRangesList name={`${optPath}.prices`} commitmentValue={commitmentValue} />}
    </TemplateSection>
  );
}
